<template>
	<div>
		<b-card>
			<template slot="header">
				<h4 class="mt-2 custom-card-header-text">Engagement</h4>
				<ul class="list-inline float-right custom-header-rightstyle header_table">
					<li v-if="communities.length>1" class="list-inline-item place-center"><label for="communityFilter" class="custom-card-header-filter label-bottom-0">Community:</label></li>
					<li v-if="communities.length>1" class="list-inline-item">
						<b-form-select v-model="selected" @change="filterByCommunity()" class="custom-card-header-filter" id="communityFilter">
							<option :value="null" selected disabled>Community</option>
							<option value="all">All</option>
							<option v-for="community in communities" :key="community._id" v-bind:value="community._id">{{ community.name}}</option>
						</b-form-select>
					</li>
					<li v-if="circles.length>=1" class="list-inline-item"><label for="circleFilter" class="custom-card-header-filter">Circle:</label></li>
					<li v-if="circles.length>=1" class="list-inline-item">
						<b-form-select v-model="selectedCircle" @change="filterByCircle()" class="custom-card-header-filter" id="circleFilter">
							<option :value="null" selected disabled>Circle</option>
							<option value="all">All</option>
							<option value="none">No Circle</option>
							<option v-for="(circle, idx) in circles" :key="`${idx}$-{circle.short}`" v-bind:value="circle.short" v-if="circle.name && circles.length>=1" >{{circle.name}}</option>
						</b-form-select>
					</li>
					<li>
						<div class="row-bak d-flex-bak align-items-center-bak">
							<datepicker @changed="dateFilter" :value="datepickerPreset"
							:initial-range="initialDateRange"
							style="padding-right: 10px;" label="Date Range"></datepicker>
						</div>
					</li>
					<li class="list-inline-item place-center"><label for="stream-action" class="custom-card-header-filter label-bottom-0">Type:</label></li>
					<li class="list-inline-item">
						<b-form-select v-model="selectedAction" v-on:change="filterByAction()" class="custom-card-header-filter" id="stream-action">
							<option :value="null" selected disabled>Action</option>
							<option v-for="(action, idx) in actions" :key="`${idx}-${action}`" v-bind:value="action">{{ action}}</option>
						</b-form-select>
					</li>
					<li class="list-inline-item place-center" v-if="selectedAction === 'Email Delivered'"><label for="stream-delivered-type" class="custom-card-header-filter label-bottom-0">Category:</label></li>
					<li class="list-inline-item" v-if="selectedAction === 'Email Delivered'">
						<b-form-select v-model="selectedDeliveredType" v-on:change="filterByAction()" class="custom-card-header-filter" id="stream-delivered-type">
							<option :value="null">All</option>
							<option v-for="(deliveredType, idx) in deliveredTypes" :key="`${idx}-${deliveredType}`" v-bind:value="deliveredType">{{ deliveredType }}</option>
						</b-form-select>
					</li>
					<!-- Unique = the Email Opens/Clicks widgets' count: one row per person per request. -->
					<li class="list-inline-item place-center" v-if="isEmailOpenOrClick"><label for="stream-email-view" class="custom-card-header-filter label-bottom-0">Show:</label></li>
					<li class="list-inline-item" v-if="isEmailOpenOrClick">
						<b-form-select v-model="emailView" v-on:change="filterByAction()" class="custom-card-header-filter" id="stream-email-view">
							<option value="unique">Unique (per person, per request)</option>
							<option value="all">All events</option>
						</b-form-select>
					</li>
					<!-- SuperAdmin only: customers always get self opt-outs (enforced in core-api too). -->
					<li class="list-inline-item place-center" v-if="isSuperAdmin && selectedAction === 'Opt Out'"><label for="stream-opt-out-scope" class="custom-card-header-filter label-bottom-0">Show:</label></li>
					<li class="list-inline-item" v-if="isSuperAdmin && selectedAction === 'Opt Out'">
						<b-form-select v-model="optOutScope" v-on:change="filterByAction()" class="custom-card-header-filter" id="stream-opt-out-scope">
							<option value="self">Self opt-outs</option>
							<option value="all">All unsubscribes</option>
						</b-form-select>
					</li>
					<li class="list-inline-item">
						<button type="button" aria-label="Export" aria-live="assertive" v-if="exportBtn" @click="exportData()" class="btn custom-card-header-filter btn-dropbox tab-focus"><i class="icon-cloud-download"></i></button>
						<button type="button" v-if="processBtn" v-b-tooltip.hover.bottom title="Generating report" class="btn custom-card-header-filter btn-dropbox"><i class="fa fa-spinner fa-spin"></i></button>
					</li>
				</ul>
			</template>
			<b-card-body>
				<search-section search-placeholder="Search Stream..." @search="handleSearch" @limit-change="handleLimitChange"/>
				<!-- The real table (header included) stays mounted and visible the whole time —
				     only its rows get masked. Keeps its internal state intact and avoids a second,
				     different-looking header. -->
				<div class="table-refresh-wrapper" :class="{ 'is-refreshing': isTableLoading }">
				<v-server-table v-if="isFiltersReady" ref="streamTable" :url="serverURL" :columns="columns" :options="options" @loaded="onLoaded" name="dataTable" id="dataTable" class="engagement-stream-table">
					<span slot="community" slot-scope="props">
                        {{ props.row.community ? props.row.community : 'N/A'}}
                    </span>
					<span slot="member" slot-scope="props" class="display-link" @click="redirectUrls(props.row.memberId)" v-if = "props.row.memberId">
                        {{ props.row.member}}
                    </span>
					<span slot="member" slot-scope="props" v-else-if = "props.row.member">
                        {{ props.row.member}}
                    </span>
					<span slot="date" slot-scope="props" v-if="props.row.date">
                    {{ props.row.date | momentDateSeconds}}
                    </span>
					<span slot="type" slot-scope="props" v-if="props.row.type" :class="[{'badge badge-primary': props.row.type == 'Request'}, {'badge badge-success': props.row.type == 'Response'}, {'badge badge-warning': props.row.type == 'Email' || props.row.type == 'Referral'}, {'badge badge-danger': props.row.type == 'Feedback'}, {'badge badge-danger': props.row.type == 'Escalation'}, {'badge badge-info': props.row.status == 'updateProfile'}, {'badge badge-danger': props.row.status == 'followedUp'}, {'badge badge-success': props.row.status == 'thankYou'},{'badge badge-info': props.row.status == 'alert'},{'badge badge-danger': props.row.status == 'optOut'},{'badge badge-resource': props.row.type == 'Resource'},{'badge badge-info': props.row.type == 'Semester Summary'}]">
                        {{ props.row.type}}
                    </span>
					<span slot="date" v-else>N/A</span>
					<span slot="view" class="action-width" slot-scope="props">
                      <b-button v-b-tooltip.hover.bottom aria-label="View the request" v-if="props.row.requestId" variant="dropbox tab-focus" size="sm" @click="openRequestInNewTab(props.row.requestId)"><i class="icon-eye"></i></b-button>
					  <b-button v-b-tooltip.hover.bottom title="View member" v-else-if="props.row.memberId" variant="dropbox tab-focus" size="sm" @click="redirectUrls(props.row.memberId)"><i class="icon-eye"></i></b-button>
                    </span>
				</v-server-table>
				</div>
			</b-card-body>
		</b-card>
	</div>
</template>

<script>
import {exportCSV, toDayParam, pickedDayToUtc, dayParamToLocal, lastNDaysStart} from '@/assets/js/common';
import axios from 'axios'
var moment = require('moment-timezone');
import VueNotifications from 'vue-notifications';
import datepicker from './components/datePicker';
import { sanitizeInput } from '../../utils/sanitizer';
import SearchSection from '@/views/engagement/components/searchSection.vue';

export default {
	name: 'stream',
	components: {
		datepicker,
		SearchSection
	},
	filters: {
		// Global momentDate + seconds: repeat events (e.g. two opens in the same minute) are
		// separate rows, and without seconds they look like duplicates.
		momentDateSeconds(value) {
			if (value && value !== 'N/A') {
				return moment(value).tz(moment.tz.guess()).format('MM/DD/YYYY HH:mm:ss');
			}
			return value;
		}
	},
	data() {
		return {
			engagement: [],
			columns: ['date', 'community', 'circleId', 'member', 'type', 'typeDetail', 'title', 'points', 'view'],
			communities: [],
			circles: [],
			isTableLoading: false,
			// Gates the table below — see created(). Without this, v-server-table mounts
			// immediately with whatever serverURL happens to be set at that instant (the
			// default, unfiltered one) and fires its own request; every later serverURL
			// reassignment during created()/getCommunities() (as community, then circle,
			// get resolved) fires yet another, uncancelled, request. Whichever response
			// happens to arrive LAST wins — not whichever was sent last — so the broader,
			// slower "everything" request can silently overwrite the correctly circle-
			// filtered one a moment later (confirmed: table shows filtered data briefly,
			// then reverts to unfiltered). responses.vue/satisfactions.vue already gate
			// their table the same way for the same reason.
			isFiltersReady: false,
			// True when the range came from the picker's "Custom dates" (local calendar days).
			isCustomRange: false,
			selected: "all",
			selectedAction: null,
			selectedDeliveredType: null,
			// Opt Out scope — 'all' is only offered to (and honored for) SuperAdmins.
			optOutScope: 'self',
			// Email Open/Click: 'unique' (matches the Dashboard widgets) or 'all' (every event).
			emailView: 'all',
			isSuperAdmin: false,
			deliveredTypes: ['Delivered Request', 'Delivered Response', 'Delivered Thank you', 'Delivered Feedback'],
			selectedCircle: "all",
			// Defaults to the last 30 days — landing here straight from the sidebar (no
			// widget, no query params) used to default to all time, which is both a slow,
			// unbounded query and a surprising number compared to every other page.
			// Overridden below in created() if startDate/endDate arrive via the URL.
			datepickerPreset: 'last-30',
			// Picker display range for a custom range forwarded in the URL.
			initialDateRange: null,
			selectedDates: {
				startDate: lastNDaysStart(30),
				endDate: new Date()
			},
			token: '',
			searchQuery: '',
			engagementCount: 0,
			actions: ['All', 'Request', 'Response', 'Feedback', 'Email Open', 'Email Click', 'Email Delivered', 'Referral', 'Update Profile', 'Thank You', 'Followed Up', 'Opt Out', 'Donor Alert', 'Volunteer Escalation','Resource', 'Semester Summary', 'Volunteer Digest'],
			serverURL: `${process.env.VUE_APP_API_BASE_URL}/dashboard/getAllEngagement`,
			options: {
				sortable: ['circleId', 'date', 'community', 'type', 'typeDetail', 'member', 'title', 'points'],
				sortIcon: {base: 'fa', up: 'fa-sort-asc', down: 'fa-sort-desc', is: 'fa-sort'},
				headings: {
					typeDetail: 'Type Detail',
					circleId: 'Circle'
				},
				pagination: {
					show: false,
					dropdown: false,
					chunk: 10,
					nav: 'fixed'
				},
				requestFunction: async function (data) {
					if (data.query) {
						// Reset if malicious content is detected
						if (data.query.toLowerCase().includes('javascript:') ||
							data.query.toLowerCase().includes('data:') ||
							data.query.toLowerCase().includes('vbscript:')) {
							data.query = '';
							// Reset the filter input
							const table = this.$parent.$refs.dataTable;
							if (table && table.$refs.filter) {
								table.$refs.filter.value = '';
							}
						}
						data.query = sanitizeInput(data.query);
					}
					this.$parent.searchQuery = data.query;
					this.$parent.isTableLoading = true;
					// vue-tables-2 applies whichever response arrives LAST, not whichever
					// request was sent last. The unfiltered "All" query (unionWith across
					// three collections) is far slower than e.g. Email Delivered + Delivered
					// Request, so changing filters right after load let the stale "All"
					// response land afterwards and overwrite the filtered rows (Clicked /
					// Delivered response rows showing under a Delivered Request filter).
					// Tag each request; drop any response that isn't the latest. Resolving
					// undefined makes vue-tables-2 skip setData (see its get-data.js).
					const requestId = (this.$parent._streamRequestSeq = (this.$parent._streamRequestSeq || 0) + 1);
					const isLatest = () => requestId === this.$parent._streamRequestSeq;
					const response = await axios.get(this.url, {headers: {'x-access-token': localStorage.getItem('token')}, params: data})
						.catch(function (e) {
							if (e.response && e.response.status === 401) {
								localStorage.removeItem('authUser');
								this.$router.push('/login');
							}
						}.bind(this));
					if (!isLatest()) return undefined;
					this.$parent.isTableLoading = false;
					return response;
				},
				responseAdapter({data}) {
					this.$parent.engagementCount = data.engagementCount;
					return {
						data: data.engagementData,
						count: data.engagementCount
					}
				},
				filterable: true,
				perPageDropdown: [],
				// Blank out the library's own "Loading..." placeholder — it rendered as a
				// second, overlapping loading message alongside our own indicator above.
				texts: { loading: '' },
				customSorting: {
					date: function (ascending) {
						return function (a, b) {
							var isValid = function (date) {
								return !isNaN(isFinite(date))
							}
							var lastA = a.date;
							var lastB = b.date;
							if (ascending && isValid(lastA) && isValid(lastB)) {
								return new Date(lastA) - new Date(lastB);
							}
							if (!ascending && isValid(lastA) && isValid(lastB)) {
								return lastA <= lastB ? 1 : -1;
							}
						}
					}
				}
			},
			exportBtn: true,
			processBtn: false
		}
	},
	notifications: {
		showWarnMsg: {
			type: VueNotifications.types.warn,
			message: "No data found for export file"
		},
		showDownloadingMsg: {
			type: VueNotifications.types.success,
			message: "Export request sent.  Data is now downloading."
		}
	},
	async created() {
		this.token = localStorage.getItem('token');
		try {
			const authUser = JSON.parse(localStorage.getItem('authUser') || 'null');
			this.isSuperAdmin = !!(authUser && authUser.role === 'SuperAdmin');
		} catch (e) {
			this.isSuperAdmin = false;
		}
		this.actions.sort();

		// Parse all query parameters first
		const communityIdParam = this.$route.params.community_id
			|| this.$route.query.community_id
			|| this.globalCommunityIdFromShell();
		const circleParam = this.$route.query.circle;
		const actionParam = this.$route.query.action;
		const startDateParam = this.$route.query.startDate;
		const endDateParam = this.$route.query.endDate;
		const presetParam = this.$route.query.preset;

		// Pre-select community (header / route / deep link) so the first table request is scoped — same pattern as requests.vue.
		if (communityIdParam && communityIdParam !== 'all') {
			this.selected = communityIdParam;
		} else {
			const activeCommunityId = localStorage.getItem('community_id');
			if (activeCommunityId) {
				this.selected = activeCommunityId;
			}
		}

		// Set circle if provided
		if (circleParam) {
			this.selectedCircle = circleParam;
		}

		// Set date range if provided. A forwarded custom range is local calendar days
		// (what the picker shows); presets are UTC days. toDateParam() re-anchors each
		// back to the same "YYYY-MM-DD" the Dashboard widget sent.
		this.isCustomRange = presetParam === 'custom';
		const parseDay = this.isCustomRange ? dayParamToLocal : (v) => new Date(v);
		if (startDateParam) {
			this.selectedDates.startDate = parseDay(startDateParam);
		}
		if (endDateParam) {
			this.selectedDates.endDate = parseDay(endDateParam);
		}
		if (this.isCustomRange && startDateParam && endDateParam) {
			this.initialDateRange = { start: dayParamToLocal(startDateParam), end: dayParamToLocal(endDateParam) };
		}

		// Set date preset so the datepicker label matches Dashboard home
		// (last-7/last-30/ytd/custom; 'all' is the picker's null "All" option)
		if (presetParam) {
			this.datepickerPreset = presetParam === 'all' ? null : presetParam;
		}

		// Set action if provided
		if (actionParam && this.actions.includes(actionParam)) {
			this.selectedAction = actionParam;
		}

		// "Email Delivered" category sub-filter (e.g. forwarded from the Alumni
		// Contacted widget, pre-selecting "Delivered Request") — only meaningful
		// when Type is actually Email Delivered.
		const deliveredTypeParam = this.$route.query.deliveredType;
		if (this.selectedAction === 'Email Delivered' && deliveredTypeParam && this.deliveredTypes.includes(deliveredTypeParam)) {
			this.selectedDeliveredType = deliveredTypeParam;
		}

		// Email Opens/Clicks widgets open the Stream in the unique view, so its row count
		// matches the widget number.
		if (this.isEmailOpenOrClick && this.$route.query.view === 'unique') {
			this.emailView = 'unique';
		}

		// Set serverURL early so it already reflects community/action/date by the time
		// the table mounts. This alone doesn't make the table's first request correct
		// though — circle isn't resolved yet at this point (see isFiltersReady below,
		// which is what actually gates the table's mount until circle is applied too).
		this.applyFilters();

		// Load communities (needed for the circle dropdown) then re-apply to confirm.
		await this.getCommunities();

		// getCommunities() calls filterByCommunity(), which unconditionally resets
		// selectedCircle to "all" and rebuilds `circles` for the resolved community —
		// re-apply the forwarded circle now that those options exist, or a
		// widget-forwarded circle is silently lost entirely (mirrors
		// requests.vue/satisfactions.vue/responses.vue, which re-apply their circle
		// param after their equivalent reset for the same reason).
		if (circleParam) {
			this.selectedCircle = circleParam;
		}

		// Re-apply after getCommunities so circle options are reflected in the URL too.
		this.applyFilters();

		// Only now does the table actually mount (see isFiltersReady/v-if above) and
		// fire its one real request, using the fully-resolved filters above — not the
		// early applyFilters() call before getCommunities(), which used to be the
		// table's first live request despite not yet knowing the correct circle.
		this.isFiltersReady = true;
	},
	computed: {
		isEmailOpenOrClick() {
			return this.selectedAction === 'Email Open' || this.selectedAction === 'Email Click';
		}
	},
	methods: {
		sanitizeTableInput(value) {
			if (!value) return '';
			// First pass of sanitization
			let sanitized = sanitizeInput(value);
			// Remove any remaining javascript: or other dangerous protocols
			sanitized = sanitized.replace(/javascript:/gi, '')
							   .replace(/data:/gi, '')
							   .replace(/vbscript:/gi, '');
			return sanitized;
		},
		handleFilterInput(event, row) {
			// Sanitize on input
			const value = event.target.value;
			const sanitized = this.sanitizeTableInput(value);
			if (value !== sanitized) {
				event.target.value = sanitized;
				row.filter = sanitized;
			}
		},
		sanitizeInput(input) {
          return sanitizeInput(input);
        },
		dateFilter(res) {
			this.selectedDates.startDate = res.startDate;
			this.selectedDates.endDate = res.endDate;
			this.isCustomRange = res.selectedValue === 'custom';
			this.applyFilters();
		},
		onLoaded(){
			this.$helpers.setTabindexOnTableCells();
			this.$helpers.setPaginationAnnouncement();
		},
		globalCommunityIdFromShell() {
			try {
				return localStorage.getItem('user_modal_option') || localStorage.getItem('community_id') || null;
			} catch (e) {
				return null;
			}
		},
		async getCommunities() {
			const {data} = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/sidebar-community`, {headers: {'x-access-token': this.token}})
			this.communities = data;
			const communityId = this.$route.params.community_id
				|| this.$route.query.community_id
				|| this.globalCommunityIdFromShell();

			if (communityId && communityId !== 'all') {
				const selectedCommunity = data.filter(community => String(community._id) === String(communityId));
				if (selectedCommunity.length >= 1) {
					this.selected = selectedCommunity[0]._id;
				} else if (this.communities.length <= 1) {
					this.selected = this.communities[0]._id;
				} else {
					this.selected = 'all';
				}
			} else if (this.communities.length <= 1) {
				this.selected = this.communities[0]._id;
			} else {
				const isValid = this.communities.some(c => String(c._id) === String(this.selected));
				if (!isValid) {
					this.selected = 'all';
				}
			}
			this.filterByCommunity();
		},
		// Send a bare "YYYY-MM-DD" day (see toDayParam in common.js), never a raw Date
		// (its .toString() isn't URL-safe) or an ISO instant (the server's time-zone
		// snap slid "00:00Z" into the previous day, so the Stream showed one more day
		// than the Reports CSV and the Dashboard widgets for the same range).
		toDateParam(date) {
			return toDayParam(this.isCustomRange ? pickedDayToUtc(date) : date);
		},
		applyFilters() {
			// Centralized method to build the serverURL with all current filters
			const baseURL = process.env.VUE_APP_API_BASE_URL;
			const params = [];

			// Type/Action filter
			if (this.selectedAction && this.selectedAction !== 'all' && this.selectedAction !== 'All') {
				params.push(`type=${this.selectedAction}`);
			}

			// "Email Delivered" category sub-filter — only meaningful (and only shown)
			// for that one Type. Clear it when Type isn't Email Delivered so a stale
			// selection can't linger and silently apply if Type is switched back later.
			if (this.selectedAction === 'Email Delivered' && this.selectedDeliveredType) {
				params.push(`deliveredType=${encodeURIComponent(this.selectedDeliveredType)}`);
			} else if (this.selectedAction !== 'Email Delivered' && this.selectedDeliveredType) {
				this.selectedDeliveredType = null;
			}

			// Opt Out scope — SuperAdmin "All unsubscribes"; everyone else gets self opt-outs.
			params.push(...this.optOutScopeParams());
			params.push(...this.emailViewParams());

			// Community filter
			if (this.selected && this.selected !== 'all') {
				params.push(`community=${this.selected}`);
			}

			// Circle filter
			if (this.selectedCircle && this.selectedCircle !== 'all') {
				params.push(`circle=${this.selectedCircle}`);
			}

			// Date range filter
			if (this.selectedDates.startDate) {
				params.push(`startDate=${this.toDateParam(this.selectedDates.startDate)}`);
			}
			if (this.selectedDates.endDate) {
				params.push(`endDate=${this.toDateParam(this.selectedDates.endDate)}`);
			}

			this.serverURL = `${baseURL}/dashboard/getAllEngagement/?${params.join('&')}`;
		},
		filterByCommunity: async function () {
			this.selectedCircle = "all";
			if (this.selected !== "all") {
				const community = this.communities.find(c => c._id == this.selected);
				if (community && community.additionalParams) {
					this.circles = community.additionalParams;
					this.circles.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
				} else {
					this.circles = [];
				}
			} else {
				this.circles = [];
			}
			this.applyFilters();
		},
		filterByCircle: async function () {
			this.applyFilters();
		},
		filterByAction: async function () {
			this.applyFilters();
		},
		// Shared by the table URL and the CSV export so they always agree.
		optOutScopeParams() {
			return (this.isSuperAdmin && this.selectedAction === 'Opt Out' && this.optOutScope === 'all')
				? ['optOutScope=all']
				: [];
		},
		// Shared by the table URL and the CSV export so they always agree.
		emailViewParams() {
			return (this.isEmailOpenOrClick && this.emailView === 'unique') ? ['unique=true'] : [];
		},
		exportData: function () {
			if (this.engagementCount > 0) {
        		this.showDownloadingMsg();
        		this.exportBtn = false;
        		this.processBtn = true;
        		// deliveredType must mirror applyFilters()'s logic exactly — it's the
        		// sub-filter that scopes "Email Delivered" down to e.g. just "Delivered
        		// Request" (how Alumni Contacted opens the Stream). Omitting it here
        		// silently widens the export to every Email Delivered row, so the CSV
        		// disagrees with what the table/widget are showing.
        		const deliveredTypeParam = (this.selectedAction === 'Email Delivered' && this.selectedDeliveredType)
        			? `&deliveredType=${encodeURIComponent(this.selectedDeliveredType)}`
        			: '';
        		const optOutScopeParam = [...this.optOutScopeParams(), ...this.emailViewParams()].map(p => `&${p}`).join('');
        		this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/getAllEngagement/?type=${this.selectedAction}&community=${this.selected}&circle=${this.selectedCircle}${deliveredTypeParam}${optOutScopeParam}&query=${this.searchQuery}&startDate=${this.toDateParam(this.selectedDates.startDate)}&endDate=${this.toDateParam(this.selectedDates.endDate)}`, {headers: {'x-access-token': this.token}}).then(async response => {
            		if (response.data.engagementData && response.data.engagementData.length) {
                		this.engagement = response.data.engagementData;

                		const desiredOrder = [
							'date', 'community', 'circleId', 'requestId', 'member',
							'email1', 'propertyId', 'member_role', 'capacity', 'propensity',
							'engagement', 'school', 'department', 'year', 'studentId',
							'type', 'typeDetail', 'title', 'text', 'recipient', 'points', 'time', 'tags',
							'memberRel',
							'customer_custom1', 'customer_custom2'
						];

						const rearrangedData = this.engagement.map(item => {
							let newItem = {};
							desiredOrder.forEach(key => {
								newItem[key] = item[key];
							});
							return newItem;
						});

                		await exportCSV(rearrangedData, 'engagement', ['location', 'source', 'memberId', '_id', 'communityId', 'status', 'queryAction', '__v'], ['date'], 'MM/DD/YYYY HH:mm:ss');
                		this.exportBtn = true;
                		this.processBtn = false;
            		} else {
                		this.showWarnMsg();
                		this.exportBtn = true;
                		this.processBtn = false;
            		}
        		}).catch(err => {
            		if (err.response.status === 401) {
                		localStorage.removeItem('authUser');
                		this.$router.push('/login');
            		}
        		});
			} else {
				this.showWarnMsg();
			}
		},
		redirectUrls: function (path) {
			window.open((`/engagement/members/${path}`), '_blank');
		},
		openRequestInNewTab(path) {
			window.open((`/engagement/requests/${path}`), '_blank');
		},
		handleSearch(searchText) {
			// Use the search text from the child component to filter your table
			this.$refs.streamTable.setFilter(searchText);
		},
		handleLimitChange(newLimit) {
			// Use the new limit value from the child to update your table
			this.$refs.streamTable.setLimit(newLimit);
		}
	}
}
</script>
<style scoped>
/* ::v-deep reaches into v-server-table's own rendered markup — it's a separate
   component, so plain scoped CSS on this file can't touch its <tbody> otherwise.
   Each cell keeps its own real, browser-computed size and position — no absolute
   positioning or header-height guessing, so this can't misalign with the real
   header the way an overlay could. Only the cell's TEXT is hidden and swapped
   for a shimmer bar drawn in the same spot; the real <thead> is never touched. */
.table-refresh-wrapper.is-refreshing ::v-deep tbody td {
	position: relative;
	color: transparent !important;
}
.table-refresh-wrapper.is-refreshing ::v-deep tbody td * {
	visibility: hidden;
}
.table-refresh-wrapper.is-refreshing ::v-deep tbody td::before {
	content: '';
	position: absolute;
	top: 6px;
	bottom: 6px;
	left: 8px;
	right: 8px;
	border-radius: 4px;
	background: linear-gradient(90deg, rgba(255, 255, 255, 0.06) 25%, rgba(255, 255, 255, 0.14) 37%, rgba(255, 255, 255, 0.06) 63%);
	background-size: 400% 100%;
	animation: table-skeleton-pulse 1.4s ease infinite;
}
@keyframes table-skeleton-pulse {
	0% { background-position: 100% 50%; }
	100% { background-position: 0% 50%; }
}
</style>
